// src/pages/Designers/DesignerEdit.jsx
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { API_URL } from '../../Redux/apiSlice';

/* endpoints */
const FILE_UPLOAD_URL = `${API_URL}/file/`;
const DESIGNER_SHOW_URL = `${API_URL}/designer`;
const DESIGNER_UPDATE_URL = `${API_URL}/designer`;
const CATEGORY_LIST_URL = `${API_URL}/designer/categories/list`;

export default function DesignerEdit() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [designer, setDesigner] = useState(null);
  const [categories, setCategories] = useState([]);

  const [form, setForm] = useState({
    name: "",
    slug: "",
    categoryId: "",
    gender: "",
    address: "",
    portfolio: "",
    metaTag: "",
    picture: null,
    picturePreview: null,
    bannerImage: null,
    bannerImagePreview: null,
  });

  const [submitting, setSubmitting] = useState(false);

  const getAuthHeader = () => {
    try {
      const raw = localStorage.getItem("knighthuntToken") || "";
      const token = raw.trim();
      if (!token) return null;
      return token.toLowerCase().startsWith("bearer ")
        ? token
        : `Bearer ${token}`;
    } catch {
      return null;
    }
  };

  /* Fetch categories */
  const fetchCategories = async () => {
    try {
      const auth = getAuthHeader();
      const resp = await fetch(CATEGORY_LIST_URL, {
        headers: auth ? { Authorization: auth } : {},
      });
      const data = await resp.json();
      setCategories(data?.response || []);
    } catch {
      setCategories([]);
    }
  };

  /* Fetch designer */
  const fetchDesigner = async () => {
    try {
      const auth = getAuthHeader();
      const resp = await fetch(`${DESIGNER_SHOW_URL}/${id}`, {
        headers: {
          "Content-Type": "application/json",
          ...(auth ? { Authorization: auth } : {}),
        },
        credentials: "include",
      });

      const data = await resp.json();
      const d = data?.response;
      console.log("designer data", d);
      setDesigner(d);

      setForm({
        name: d?.name || "",
        slug: d?.slug || "",
        categoryId: d?.category?.id || "",
        gender: d?.gender || "",
        address: d?.address || "",
        portfolio: d?.portfolio || "",
        metaTag: d?.metaTag || "",
        picture: null,
        picturePreview: d?.pic || null,
        bannerImage: null,
        bannerImagePreview: d?.bannerImage || null,
      });
    } catch (err) {
      console.error("Error fetching designer:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchDesigner();
  }, []);

  /* Image picker (picture / banner) */
  const pickImage = (e, type) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const preview = URL.createObjectURL(file);

    setForm((prev) => ({
      ...prev,
      [type]: file,
      [`${type}Preview`]: preview,
    }));
  };

  /* Upload image */
  const uploadWithFetch = async (file) => {
    const fd = new FormData();
    fd.append("image", file);

    const auth = getAuthHeader();
    const resp = await fetch(FILE_UPLOAD_URL, {
      method: "POST",
      body: fd,
      headers: auth ? { Authorization: auth } : {},
      credentials: "include",
    });

    const body = await resp.json();
    return body?.response?.url || body?.data?.url || body?.url || "";
  };

  /* Submit update */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      let pictureUrl = designer.pic;
      let bannerUrl = designer.banner;

      if (form.picture) {
        pictureUrl = await uploadWithFetch(form.picture);
      }

      if (form.bannerImage) {
        bannerUrl = await uploadWithFetch(form.bannerImage);
      }

      const payload = {
        id,
        name: form.name,
        slug: form.slug,
        categoryId: form.categoryId,
        gender: form.gender,
        address: form.address,
        portfolio: form.portfolio,
        metaTag: form.metaTag,
        pic: pictureUrl,
        bannerImage: bannerUrl,
      };

      const auth = getAuthHeader();

      const resp = await fetch(DESIGNER_UPDATE_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(auth ? { Authorization: auth } : {}),
        },
        body: JSON.stringify(payload),
        credentials: "include",
      });

      const data = await resp.json();

      if (data?.status) {
        alert("Designer updated successfully!");
        navigate("/designer-list");
      } else {
        alert(data?.message || "Update failed");
      }
    } catch (err) {
      console.error("Update error:", err);
      alert("Something went wrong");
    }

    setSubmitting(false);
  };

  if (loading) return <p>Loading designer...</p>;
  if (!designer) return <p>Designer not found</p>;

  return (
    <div style={{ padding: 20, paddingTop: 100, maxWidth: 700, margin: "0 auto" }}>
      <form
        onSubmit={handleSubmit}
        style={{
          padding: 20,
          borderRadius: 10,
          border: "1px solid #d7dde0",
          background: "#fafafa",
        }}
      >
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontWeight: 600 }}>Name </label>
          <input
            className="form-control "
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Name"
            required
            style={{ marginTop: 6 }}
          />
        </div>

        {/* Slug */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontWeight: 600 }}>Slug </label>
          <input
            className="form-control "
            value={form.slug}
            onChange={(e) => setForm({ ...form, slug: e.target.value })}
            placeholder="Slug"
            required
            style={{ marginTop: 6 }}
          />
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontWeight: 600 }}>Category</label>
          <select
            className="form-control "
            value={form.categoryId}
            onChange={(e) => setForm({ ...form, categoryId: e.target.value })}
            style={{ marginTop: 6 }}
            required
          >
            <option value="">Select Category</option>
            {categories.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.name}
              </option>
            ))}
          </select>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ fontWeight: 600 }}>Gender</label>
          <select
            className="form-control "
            value={form.gender}
            onChange={(e) => setForm({ ...form, gender: e.target.value })}
            style={{ marginTop: 6 }}
          >
            <option value="">Select Gender</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
            <option value="unisex">Unisex</option>
          </select>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ fontWeight: 600 }}>Address</label>
          <textarea
            className="form-control "
            value={form.address}
            onChange={(e) => setForm({ ...form, address: e.target.value })}
            rows={3}
            placeholder="Address"
            style={{ marginTop: 6 }}
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ fontWeight: 600 }}>Portfolio</label>
          <input
            className="form-control "
            value={form.portfolio}
            onChange={(e) => setForm({ ...form, portfolio: e.target.value })}
            placeholder="Portfolio"
            style={{ marginTop: 6 }}
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ fontWeight: 600 }}>Meta Tag</label>
          <input
            className="form-control "
            value={form.metaTag}
            onChange={(e) => setForm({ ...form, metaTag: e.target.value })}
            placeholder="Meta Tag"
            style={{ marginTop: 6 }}
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ fontWeight: 600 }}>Picture</label>
          <input
            type="file"
            className="form-control "
            onChange={(e) => pickImage(e, "picture")}
            style={{ marginTop: 6 }}
          />
          {form.picturePreview && (
            <img src={form.picturePreview} alt="pic" width={100} className="mb-3" />
          )}
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ fontWeight: 600 }}>Banner</label>
          <input
            type="file"
            className="form-control "
            onChange={(e) => pickImage(e, "bannerImage")}
            style={{ marginTop: 6 }}
          />
          {form.bannerImagePreview && (
            <img
              src={form.bannerImagePreview}
              alt="banner"
              style={{
                width: "100%",
                height: 150,
                objectFit: "cover",
                borderRadius: 8,
                marginBottom: 16,
              }}
            />
          )}
        </div>

        <button className="btn btn-primary w-100" disabled={submitting}>
          {submitting ? "Updating..." : "Update Designer"}
        </button>
      </form>
    </div>
  );
}
